import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Breadcrumb } from '../ui/breadcrumb';
import { FileUpload } from '../ui/FileUpload';
import { ArrowLeft, Upload, Calendar, User, MapPin, Phone, Mail, FileText, BookOpen, Zap, Train } from 'lucide-react';
import { AgreementDialog } from './AgreementDialog';
import { motion } from 'framer-motion';
import { learnEarnAPI } from '../../services/api';
import { validationUtils } from '../../utils/validationUtils';
import { pricingUtils } from '../../utils/pricingUtils';
import { initiateOTSPayment, formatAmount } from '../../utils/razorpayPayment';

const LOCAL_TRAIN_STATIONS = [
  // Central Line - Up (from Kalyan)
  "Kalyan","Shahad","Ambivli","Titwala","Khadavli","Vasind","Asangaon","Atgaon",
  "Thansit","Khardi","Umbermali","Kasara",
  // Central Line - Down (towards CSMT)
  "Vithalwadi","Ulhasnagar","Ambarnath","Badlapur","Vangani","Neral","Bhivpuri Road",
  "Karjat","Palasdari","Kelavli","Dolavli","Lowjee","Khopoli",
  "Thakurli","Dombivli","Kopar","Diva","Mumbra","Kalwa","Thane","Mulund","Nahur",
  "Bhandup","Kanjurmarg","Vikhroli","Ghatkopar","Vidyavihar","Kurla","Kings Circle",
  "Sion","Matunga","Dadar","Parel","Currey Road","Chinchpokli","Byculla",
  "Sandhurst Road","Masjid","CSMT",
  // Western Line
  "Churchgate","Marine Lines","Charni Road","Grant Road","Mumbai Central","Mahalaxmi",
  "Lower Parel","Prabhadevi","Matunga Road","Mahim","Bandra","Khar Road",
  "Santacruz","Vile Parle","Andheri","Jogeshwari","Goregaon","Malad","Kandivali",
  "Borivali","Dahisar","Mira Road","Bhayandar","Naigaon","Vasai Road","Nallasopara",
  "Virar","Vaitarna","Saphale","Kelve Road","Palghar","Umroli","Boisar","Vangaon",
  "Dahanu Road",
  // Vasai-Panvel Branch
  "Juchandra","Kaman Road","Kharbao","Bhiwandi Road",
  "Dativali","Nilaje","Taloja Panchanand","Navade Road","Kalamboli","Panvel",
  // Harbour Line
  "Dockyard Road","Reay Road","Cotton Green","Sewri","Wadala Road","GTB Nagar",
  "Chunabhatti","Tilaknagar","Chembur","Govandi","Mankhurd","Vashi","Sanpada",
  "Juinagar","Nerul","CBD Belapur","Kharghar","Mansarovar","Khandeshwar",
  // Uran Line
  "Targhar","Bamandongri","Kharkopar","Gavhan","Shematikhar","Nhava-Sheva",
  "Dronagiri","Uran",
  // Harbour - Airoli branch
  "Airoli","Rabale","Ghansoli","Koparkhairane","Turbhe",
];

const METRO_STATIONS = [
  // Metro Line 1 (Versova–Andheri–Ghatkopar)
  "Ghatkopar","Jagruti Nagar","Asalpha","Saki Naka","Marol Naka","Airport Road",
  "Chakala","Western Express Highway","Andheri 2","Azad Nagar","DN Nagar","Versova",
  // Metro Line 2A/7
  "Andheri West","Lower Oshiwara","Oshiwara","Goregaon West","Bangur Nagar",
  "Lower Malad","Malad West","Valnai-Meeth Chowky","Dahanukarwadi","Kandivali West",
  "Shimpoli","Borivali West","Eksar","Mandapeshwar","Kandarpada","Anand Nagar",
  "Dahisar East","Ovaripada","Rashtriya Udyan","Devipada","Magathane","Poisar",
  "Akurli","Kurar","Dindoshi","Aarey","Goregaon East","Jogeshwari East","Mogra","Gundavali",
  // Metro Line 3 (Aqua Line)
  "Aarey JVLR","Seepz","MIDC - Andheri","Csmi Airport T2","Sahar Road",
  "Csmi Airport T1","Santacruz Metro","Bandra Colony","BKC","Dharavi",
  "Shitala Devi Mandir","Dadar Metro","Siddhivinayak","Worli","Acharya Atre Chowk",
  "Science Metro","Mahalaxmi Metro","Jss Metro","Grant Road Metro","Girgaon",
  "Kalbadevi","Csmt Metro","Hutatma Chowk","Churchgate Metro","Vidhan Bhavan","Cuffe Parade",
  // Metro Line 4 (Navi Mumbai)
  "Belapur Terminal","RBI Colony","Belpada","Utsav Chowk","Kendriya Vihar",
  "Kharghar Village","Central Park","Pethpada","Amandoot","Pethali Taloja","Pendhar",
];

const MONORAIL_STATIONS = [
  "Chembur Mono","VNP Marg Junction","Fertiliser Township","Bharat Petroleum",
  "Mysore Colony","Bhakti Park","Wadala","GTB Nagar Mono","Antop Hill",
  "Acharya Atre Nagar","Wadala Bridge","Vitthal Mandir","Naigaon Mono",
  "Ambedkar Nagar","Mint Colony","Lower Parel Mono","Sant Gadge Maharaj",
];

// Delivery charges map (station name lowercase → price in ₹)
const DELIVERY_CHARGES = {
  // ── LOCAL TRAIN ──
  "kalyan": 0,
  // Central line up (Kasara direction)
  "shahad": 15, "ambivli": 15,
  "titwala": 25, "khadavli": 25, "vasind": 25,
  "asangaon": 40, "atgaon": 40, "thansit": 40, "khardi": 40,
  "umbermali": 50, "kasara": 50,
  // Central line - Karjat branch
  "vithalwadi": 20, "ulhasnagar": 20, "ambarnath": 20,
  "badlapur": 30, "vangani": 30,
  "neral": 40, "bhivpuri road": 40, "karjat": 40, "palasdari": 40, "palasdhari": 40, "kelavli": 40,
  "dolavli": 60, "lowjee": 60, "khopoli": 60,
  // Central line down - towards CSMT
  "thakurli": 15, "dombivli": 15, "kopar": 15,
  "diva": 30, "mumbra": 30, "kalwa": 30, "thane": 30, "mulund": 30, "nahur": 30,
  "bhandup": 30, "kanjurmarg": 30,
  "vikhroli": 40, "ghatkopar": 40, "vidyavihar": 40, "kurla": 40, "kings circle": 40,
  "sion": 40, "matunga": 40, "dadar": 40,
  "parel": 50, "currey road": 50, "chinchpokli": 50, "byculla": 50,
  "sandhurst road": 50, "masjid": 50, "csmt": 50,
  // Western line
  "prabhadevi": 60, "lower parel": 60, "mahalaxmi": 60, "mumbai central": 60,
  "grant road": 80, "charni road": 80, "marine lines": 80, "churchgate": 80,
  "matunga road": 50, "mahim": 50, "bandra": 50, "khar road": 50,
  "santacruz": 50, "vile parle": 50,
  "andheri": 60, "jogeshwari": 60, "goregaon": 60, "malad": 60,
  "kandivali": 60, "borivali": 60, "dahisar": 60, "mira road": 60,
  "bhayandar": 70, "naigaon": 70, "vasai road": 70, "nallasopara": 70, "virar": 70,
  "vaitarna": 90, "saphale": 90, "kelve road": 90,
  "palghar": 100, "umroli": 100, "boisar": 100,
  "vangaon": 110, "dahanu road": 110,
  // Vasai–Panvel branch
  "juchandra": 50, "kaman road": 50, "kharbao": 50, "bhiwandi road": 50,
  "dativali": 50, "nilaje": 50, "taloja panchanand": 50, "navade road": 50,
  "kalamboli": 50, "panvel": 50,
  // Harbour line
  "chunabhatti": 50, "gtb nagar": 50, "wadala road": 50, "sewri": 50,
  "cotton green": 60, "reay road": 60, "dockyard road": 60,
  "tilaknagar": 50, "chembur": 50, "govandi": 50, "mankhurd": 50,
  "airoli": 40, "rabale": 40,
  "ghansoli": 50, "koparkhairane": 50, "turbhe": 50,
  "sanpada": 60, "vashi": 60, "juinagar": 60, "nerul": 60, "cbd belapur": 60,
  "kharghar": 70, "mansarovar": 70, "khandeshwar": 70,
  // Uran line
  "targhar": 60, "bamandongri": 60, "kharkopar": 60, "gavhan": 60,
  "shematikhar": 70, "nhava-sheva": 70, "dronagiri": 70, "uran": 70,
  // ── METRO LINE 1 (Versova–Andheri–Ghatkopar) ──
  "jagruti nagar": 60, "asalpha": 60,
  "saki naka": 80, "marol naka": 80, "airport road": 80,
  "chakala": 100, "western express highway": 100, "andheri 2": 100,
  "azad nagar": 120, "dn nagar": 120, "versova": 120,
  // ── METRO LINE 2A / 7 ──
  "andheri west": 60, "lower oshiwara": 60, "oshiwara": 60,
  "goregaon west": 80, "bangur nagar": 80, "lower malad": 80, "malad west": 80,
  "valnai-meeth chowky": 80, "dahanukarwadi": 80, "kandivali west": 80, "shimpoli": 80,
  "borivali west": 100, "eksar": 100, "mandapeshwar": 100, "kandarpada": 100, "anand nagar": 100,
  "dahisar east": 100,
  "ovaripada": 130, "rashtriya udyan": 130, "devipada": 130, "magathane": 130, "poisar": 130,
  "akurli": 150, "kurar": 150, "dindoshi": 150, "aarey": 150, "goregaon east": 150,
  "jogeshwari east": 170, "mogra": 170, "gundavali": 170,
  // ── METRO LINE 3 (Aqua Line) ──
  "aarey jvlr": 150, "seepz": 150, "midc - andheri": 150, "csmi airport t2": 150,
  "sahar road": 130, "csmi airport t1": 130,
  "santacruz metro": 100, "bandra colony": 100, "bkc": 100,
  "dharavi": 80,
  "shitala devi mandir": 60,
  "dadar metro": 40,
  "siddhivinayak": 60,
  "worli": 80, "acharya atre chowk": 80,
  "science metro": 100, "mahalaxmi metro": 100, "jss metro": 100,
  "grant road metro": 130, "girgaon": 130, "kalbadevi": 130, "csmt metro": 130,
  "hutatma chowk": 150, "churchgate metro": 150, "vidhan bhavan": 150, "cuffe parade": 150,
  // ── METRO LINE 4 (Navi Mumbai) ──
  "belapur terminal": 60,
  "rbi colony": 80, "belpada": 80,
  "utsav chowk": 90, "kendriya vihar": 90,
  "kharghar village": 100, "central park": 100,
  "pethpada": 110, "amandoot": 110,
  "pethali taloja": 120, "pendhar": 120,
  // ── MONORAIL ──
  "chembur mono": 60, "vnp marg junction": 60, "fertiliser township": 60, "bharat petroleum": 60,
  "mysore colony": 80, "bhakti park": 80, "wadala": 80, "gtb nagar mono": 80,
  "antop hill": 80, "acharya atre nagar": 80,
  "wadala bridge": 110, "vitthal mandir": 110, "naigaon mono": 110,
  "ambedkar nagar": 110, "mint colony": 110,
  "lower parel mono": 120, "sant gadge maharaj": 120,
};

const getDeliveryCharge = (station) => {
  if (!station) return null;
  return DELIVERY_CHARGES[station.toLowerCase()] ?? null;
};

function DeliverySection({ deliveryType, setDeliveryType, destination, setDestination, trainType, setTrainType, errors }) {
  const [search, setSearch] = React.useState('');

  // Clear train type and destination when delivery type changes
  const handleDeliveryTypeChange = (type) => {
    setDeliveryType(type);
    setTrainType('');
    setDestination('');
    setSearch('');
  };

  const isCollege = deliveryType === 'college-paper';
  const chargeMultiplier = isCollege ? 2 : 1;

  const stationPool = trainType === 'local' ? LOCAL_TRAIN_STATIONS
    : trainType === 'metro' ? METRO_STATIONS
    : trainType === 'monorail' ? MONORAIL_STATIONS
    : [];

  const filtered = stationPool.filter(s =>
    s.toLowerCase().includes(search.toLowerCase())
  );

  const trainConfig = {
    local:    { label: 'Local Train', color: 'blue',   bg: 'bg-blue-600',   border: 'border-blue-500',   light: 'bg-blue-50',   text: 'text-blue-700'   },
    metro:    { label: 'Metro Rail',  color: 'violet', bg: 'bg-violet-600', border: 'border-violet-500', light: 'bg-violet-50', text: 'text-violet-700' },
    monorail: { label: 'Mono Rail',   color: 'orange', bg: 'bg-orange-500', border: 'border-orange-500', light: 'bg-orange-50', text: 'text-orange-700' },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-5 p-6 bg-indigo-50 rounded-2xl border border-indigo-200"
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
          <Train className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-indigo-700">Delivery System</h3>
      </div>

      {/* Paper type buttons */}
      <div>
        <Label className="text-foreground font-semibold mb-3 block">Select Paper Type *</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Normal Papers */}
          <button
            type="button"
            onClick={() => handleDeliveryTypeChange('normal-paper')}
            className={`p-4 sm:p-5 rounded-xl border-2 text-left transition-all duration-200 ${deliveryType === 'normal-paper' ? 'border-indigo-500 bg-indigo-50 shadow-md' : 'border-gray-200 bg-white hover:border-indigo-300'}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-indigo-700 text-sm">Normal Papers Used</span>
              {deliveryType === 'normal-paper' && <span className="ml-auto text-xs bg-indigo-500 text-white px-2 py-0.5 rounded-full">✓</span>}
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Standard plain papers will be used for writing. Delivery from <span className="font-semibold text-indigo-600">Kalyan</span> to your selected station.
            </p>
          </button>

          {/* College Papers */}
          <button
            type="button"
            onClick={() => handleDeliveryTypeChange('college-paper')}
            className={`p-4 sm:p-5 rounded-xl border-2 text-left transition-all duration-200 ${deliveryType === 'college-paper' ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 bg-white hover:border-purple-300'}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-purple-700 text-sm">College Papers Strictly Used</span>
              {deliveryType === 'college-paper' && <span className="ml-auto text-xs bg-purple-500 text-white px-2 py-0.5 rounded-full">✓</span>}
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Official college-format papers strictly used as required. Delivery from <span className="font-semibold text-purple-600">Kalyan</span> to your selected station.
            </p>
          </button>
        </div>
        {!deliveryType && <p className="text-orange-500 text-xs mt-2 font-medium">⚠ Please select a paper type.</p>}
      </div>

      {/* Train type + destination — shown after paper type selected */}
      {deliveryType && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

          {/* Train type question */}
          <div>
            <Label className="text-foreground font-semibold mb-3 block">
              On which railway station do you want delivery? From which train? *
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {(['local','metro','monorail']).map((type) => {
                const cfg = trainConfig[type];
                const selected = trainType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => { setTrainType(type); setDestination(''); setSearch(''); }}
                    className={`p-2 sm:p-3 rounded-xl border-2 text-center transition-all duration-200 min-w-0 overflow-hidden ${selected ? `${cfg.border} ${cfg.light} shadow-md` : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  >
                    <div className={`w-8 h-8 ${cfg.bg} rounded-lg flex items-center justify-center mx-auto mb-1`}>
                      <Train className="w-4 h-4 text-white" />
                    </div>
                    <p className={`text-xs font-bold leading-snug w-full overflow-hidden text-ellipsis whitespace-nowrap ${selected ? cfg.text : 'text-gray-700'}`}>{cfg.label}</p>
                    {selected && <span className={`text-xs ${cfg.text}`}>✓</span>}
                  </button>
                );
              })}
            </div>
            {errors.trainType && !trainType && <p className="text-orange-500 text-xs mt-2 font-medium">⚠ {errors.trainType}</p>}
          </div>

          {/* Station selector — shown after train type selected */}
          {trainType && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              {/* Route info */}
              <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-indigo-200">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <MapPin className="w-4 h-4 text-green-600" />
                  <span className="text-green-700">From: Kalyan</span>
                </div>
                <div className="flex-1 border-t-2 border-dashed border-indigo-300 mx-2" />
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <MapPin className="w-4 h-4 text-red-500" />
                  <span className="text-indigo-700">{destination ? `To: ${destination}` : 'To: Select Station'}</span>
                </div>
              </div>

              <Label className="text-foreground font-semibold block">
                Select Destination Station *{' '}
                <span className="text-xs font-normal text-gray-400">({trainConfig[trainType].label} stations)</span>
              </Label>

              {/* Search */}
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="🔍 Search station..."
                className="w-full px-4 py-2 border border-indigo-200 rounded-xl bg-white text-sm focus:outline-none focus:border-indigo-500"
              />

              {/* Station grid */}
              <div className="max-h-52 overflow-y-auto rounded-xl border border-indigo-200 bg-white p-2 grid grid-cols-2 md:grid-cols-3 gap-1.5">
                {filtered.map((station) => {
                  const charge = getDeliveryCharge(station);
                  return (
                    <button
                      key={station}
                      type="button"
                      onClick={() => { setDestination(station); setSearch(''); }}
                      className={`px-3 py-2 rounded-lg text-xs font-medium text-left transition-all duration-150 ${
                        destination === station
                          ? `${trainConfig[trainType].bg} text-white shadow-sm`
                          : `${trainConfig[trainType].light} ${trainConfig[trainType].text} hover:opacity-80`
                      }`}
                    >
                      <span className="block leading-tight">{station}</span>
                      {charge !== null && (
                        <span className={`text-xs font-bold mt-0.5 block ${destination === station ? 'text-white/80' : 'text-orange-500'}`}>
                          {charge === 0 ? 'Free' : `₹${charge * chargeMultiplier}`}
                        </span>
                      )}
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <p className="col-span-3 text-center text-gray-400 text-xs py-4">No station found</p>
                )}
              </div>

              {/* Delivery confirmation + charge */}
              {destination && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-2"
                >
                  <div className={`p-3 ${trainConfig[trainType].bg} rounded-xl text-white text-sm font-semibold flex items-center gap-2`}>
                    <Train className="w-4 h-4" />
                    {trainConfig[trainType].label}: Kalyan → {destination}
                  </div>
                  {(() => {
                    const charge = getDeliveryCharge(destination);
                    if (charge === null) return null;
                    const finalCharge = charge * chargeMultiplier;
                    if (finalCharge === 0) return (
                      <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl flex items-center gap-2">
                        <span className="text-emerald-600 text-lg">🎉</span>
                        <div>
                          <p className="text-emerald-700 font-bold text-sm">Free Delivery!</p>
                          <p className="text-emerald-600 text-xs">No delivery charges for Kalyan station</p>
                        </div>
                      </div>
                    );
                    return (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-orange-500 text-lg">🚚</span>
                          <div>
                            <p className="text-orange-700 font-bold text-sm">Delivery Charges</p>
                            <p className="text-orange-600 text-xs">Kalyan → {destination}</p>
                            {isCollege && (
                              <p className="text-purple-600 text-xs font-medium mt-0.5">College papers rate (×2)</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xl font-black text-orange-600">₹{finalCharge}</span>
                      </div>
                    );
                  })()}
                </motion.div>
              )}

              {errors.destination && (
                <p className="text-destructive text-sm flex items-center gap-1">⚠ {errors.destination}</p>
              )}
            </motion.div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

function JournalDeliverySection({ destination, setDestination, trainType, setTrainType, errors }) {
  const [search, setSearch] = React.useState('');

  const stationPool = trainType === 'local' ? LOCAL_TRAIN_STATIONS
    : trainType === 'metro' ? METRO_STATIONS
    : trainType === 'monorail' ? MONORAIL_STATIONS
    : [];

  const filtered = stationPool.filter(s =>
    s.toLowerCase().includes(search.toLowerCase())
  );

  const trainConfig = {
    local:    { label: 'Local Train', bg: 'bg-blue-600',   border: 'border-blue-500',   light: 'bg-blue-50',   text: 'text-blue-700'   },
    metro:    { label: 'Metro Rail',  bg: 'bg-violet-600', border: 'border-violet-500', light: 'bg-violet-50', text: 'text-violet-700' },
    monorail: { label: 'Mono Rail',   bg: 'bg-orange-500', border: 'border-orange-500', light: 'bg-orange-50', text: 'text-orange-700' },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-5 p-6 bg-indigo-50 rounded-2xl border border-indigo-200"
    >
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
          <Train className="w-4 h-4 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-indigo-700">Delivery System</h3>
      </div>

      {/* Info banner */}
      <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl text-xs text-purple-700 font-medium">
        📦 Journal delivery charges are calculated at <span className="font-black">2× the standard rate</span> due to special handling.
      </div>

      {/* Train type selector */}
      <div>
        <Label className="text-foreground font-semibold mb-3 block">
          On which railway station do you want delivery? From which train? *
        </Label>
        <div className="grid grid-cols-3 gap-2">
          {(['local','metro','monorail']).map((type) => {
            const cfg = trainConfig[type];
            const selected = trainType === type;
            return (
              <button
                key={type}
                type="button"
                onClick={() => { setTrainType(type); setDestination(''); setSearch(''); }}
                className={`p-2 sm:p-3 rounded-xl border-2 text-center transition-all duration-200 min-w-0 overflow-hidden ${selected ? `${cfg.border} ${cfg.light} shadow-md` : 'border-gray-200 bg-white hover:border-gray-300'}`}
              >
                <div className={`w-8 h-8 ${cfg.bg} rounded-lg flex items-center justify-center mx-auto mb-1`}>
                  <Train className="w-4 h-4 text-white" />
                </div>
                <p className={`text-xs font-bold leading-snug w-full overflow-hidden text-ellipsis whitespace-nowrap ${selected ? cfg.text : 'text-gray-700'}`}>{cfg.label}</p>
                {selected && <span className={`text-xs ${cfg.text}`}>✓</span>}
              </button>
            );
          })}
        </div>
        {errors.trainType && !trainType && <p className="text-orange-500 text-xs mt-2 font-medium">⚠ {errors.trainType}</p>}
      </div>

      {trainType && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          {/* Route info */}
          <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-indigo-200">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <MapPin className="w-4 h-4 text-green-600" />
              <span className="text-green-700">From: Kalyan</span>
            </div>
            <div className="flex-1 border-t-2 border-dashed border-indigo-300 mx-2" />
            <div className="flex items-center gap-2 text-sm font-semibold">
              <MapPin className="w-4 h-4 text-red-500" />
              <span className="text-indigo-700">{destination ? `To: ${destination}` : 'To: Select Station'}</span>
            </div>
          </div>

          <Label className="text-foreground font-semibold block">
            Select Destination Station *{' '}
            <span className="text-xs font-normal text-gray-400">({trainConfig[trainType].label} stations)</span>
          </Label>

          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Search station..."
            className="w-full px-4 py-2 border border-indigo-200 rounded-xl bg-white text-sm focus:outline-none focus:border-indigo-500"
          />

          <div className="max-h-52 overflow-y-auto rounded-xl border border-indigo-200 bg-white p-2 grid grid-cols-2 md:grid-cols-3 gap-1.5">
            {filtered.map((station) => {
              const baseCharge = getDeliveryCharge(station);
              const charge = baseCharge !== null ? baseCharge * 2 : null;
              return (
                <button
                  key={station}
                  type="button"
                  onClick={() => { setDestination(station); setSearch(''); }}
                  className={`px-3 py-2 rounded-lg text-xs font-medium text-left transition-all duration-150 ${
                    destination === station
                      ? `${trainConfig[trainType].bg} text-white shadow-sm`
                      : `${trainConfig[trainType].light} ${trainConfig[trainType].text} hover:opacity-80`
                  }`}
                >
                  <span className="block leading-tight">{station}</span>
                  {charge !== null && (
                    <span className={`text-xs font-bold mt-0.5 block ${destination === station ? 'text-white/80' : 'text-orange-500'}`}>
                      {charge === 0 ? 'Free' : `₹${charge}`}
                    </span>
                  )}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="col-span-3 text-center text-gray-400 text-xs py-4">No station found</p>
            )}
          </div>

          {destination && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-2">
              <div className={`p-3 ${trainConfig[trainType].bg} rounded-xl text-white text-sm font-semibold flex items-center gap-2`}>
                <Train className="w-4 h-4" />
                {trainConfig[trainType].label}: Kalyan → {destination}
              </div>
              {(() => {
                const base = getDeliveryCharge(destination);
                if (base === null) return null;
                const finalCharge = base * 2;
                if (finalCharge === 0) return (
                  <div className="p-3 bg-emerald-50 border border-emerald-300 rounded-xl flex items-center gap-2">
                    <span className="text-emerald-600 text-lg">🎉</span>
                    <div>
                      <p className="text-emerald-700 font-bold text-sm">Free Delivery!</p>
                      <p className="text-emerald-600 text-xs">No delivery charges for Kalyan station</p>
                    </div>
                  </div>
                );
                return (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-orange-500 text-lg">🚚</span>
                      <div>
                        <p className="text-orange-700 font-bold text-sm">Delivery Charges</p>
                        <p className="text-orange-600 text-xs">Kalyan → {destination}</p>
                        <p className="text-purple-600 text-xs font-medium mt-0.5">Journal rate (×2)</p>
                      </div>
                    </div>
                    <span className="text-xl font-black text-orange-600">₹{finalCharge}</span>
                  </div>
                );
              })()}
            </motion.div>
          )}

          {errors && errors.destination && (
            <p className="text-destructive text-sm flex items-center gap-1">⚠ {errors.destination}</p>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

export function OTSService() {
  const navigate = useNavigate();

  const breadcrumbItems = [
    { label: 'Learn & Earn', href: '/learn' },
    { label: 'Customer Work', href: '/learn/customer-work' },
    { label: 'OTS Service' }
  ];

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    whatsapp: '',
    sameAsPhone: false,
    email: '',
    workType: 'assignment',
    pages: '',
    amount: '',
    deadline: '',
    additionalInfo: '',
    agreementAccepted: false,
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [showAgreement, setShowAgreement] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null); // null | 'cancelled' | 'failed'
  const [serviceType, setServiceType] = useState(''); // 'normal' | 'urgent'
  const [deliveryType, setDeliveryType] = useState(''); // 'normal-paper' | 'college-paper'
  const [destination, setDestination] = useState('');
  const [trainType, setTrainType] = useState('');         // lifted: 'local'|'metro'|'monorail'
  const [journalDestination, setJournalDestination] = useState('');
  const [journalTrainType, setJournalTrainType] = useState('');
  const [paymentDone, setPaymentDone] = useState(false);

  // Clear file error as soon as a file is selected
  const handleFileSelect = (file) => {
    setUploadedFile(file);
    // Always clear both file error and train errors reactively so stale
    // validation messages disappear the moment the user fixes them
    setErrors(prev => ({ ...prev, file: file ? '' : prev.file }));
  };

  // Clear train error as soon as a train type is picked
  const handleTrainTypeChange = (type) => {
    setTrainType(type);
    setDestination('');
    setErrors(prev => ({ ...prev, trainType: '', destination: '' }));
  };

  const handleJournalTrainTypeChange = (type) => {
    setJournalTrainType(type);
    setJournalDestination('');
    setErrors(prev => ({ ...prev, trainType: '', destination: '' }));
  };

  // Helper: days from today to selected deadline
  const getDaysUntilDeadline = (deadlineStr) => {
    if (!deadlineStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(deadlineStr);
    deadline.setHours(0, 0, 0, 0);
    return Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
  };

  // Urgent surcharge per page based on days — ASSIGNMENT
  const getUrgentRatePerPage = (days) => {
    if (days === 1) return 12;
    if (days === 2) return 10;
    if (days === 3) return 9;
    return 7;
  };

  // Urgent surcharge per page based on days — JOURNAL
  const getUrgentJournalRatePerPage = (days) => {
    if (days === 1) return 9;
    if (days === 2) return 8;
    if (days === 3) return 7;
    return 5;
  };

  const daysUntilDeadline = getDaysUntilDeadline(formData.deadline);

  // Is upload allowed based on service type & deadline?
  const uploadAllowed = (() => {
    if (!serviceType || !formData.deadline) return false;
    if (serviceType === 'normal') return daysUntilDeadline >= 4;
    if (serviceType === 'urgent') return daysUntilDeadline >= 1 && daysUntilDeadline <= 3;
    return false;
  })();

  const uploadBlockMessage = (() => {
    if (!serviceType || !formData.deadline) return null;
    if (serviceType === 'normal' && daysUntilDeadline < 4)
      return `⚠️ Deadline is only ${daysUntilDeadline} day(s) away. Normal service requires at least 4 days. Please choose Urgent Service or extend your deadline.`;
    if (serviceType === 'urgent' && daysUntilDeadline > 3)
      return `⚠️ Deadline is ${daysUntilDeadline} days away. Urgent service is only for deadlines within 3 days. Please choose Normal Service.`;
    return null;
  })();

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'sameAsPhone' && value === true ? { whatsapp: prev.phone } : {}),
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!validationUtils.validatePhone(formData.phone)) newErrors.phone = 'Phone number must be exactly 10 digits';
    if (formData.whatsapp && !validationUtils.validatePhone(formData.whatsapp)) newErrors.whatsapp = 'WhatsApp number must be exactly 10 digits';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!validationUtils.validateEmail(formData.email)) newErrors.email = 'Please enter a valid email';
    if (!formData.deadline) newErrors.deadline = 'Deadline is required';

    if (formData.workType === 'assignment') {
      if (!formData.pages.trim() || parseInt(formData.pages) <= 0) {
        newErrors.pages = 'Please enter a valid number of pages';
      }
    } else {
      if (!formData.pages.trim() || parseInt(formData.pages) <= 0) {
        newErrors.pages = 'Please enter a valid number of pages';
      }
    }

    if (!uploadedFile) {
      newErrors.file = 'Please upload the required file';
    }

    // Train type validation — only when delivery section is visible
    if (formData.workType === 'assignment') {
      if (!trainType) newErrors.trainType = 'Please select a train type.';
      if (trainType && !destination) newErrors.destination = 'Please select a destination station.';
    }
    if (formData.workType === 'journal') {
      if (!journalTrainType) newErrors.trainType = 'Please select a train type.';
      if (journalTrainType && !journalDestination) newErrors.destination = 'Please select a destination station.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (isSubmitting) return;
    // Submit the form directly (payment is handled separately via Pay button)
    setIsSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('address', formData.address);
      submitData.append('phone', formData.phone);
      submitData.append('whatsapp', formData.whatsapp);
      submitData.append('email', formData.email);
      submitData.append('workType', formData.workType);
      submitData.append('deadline', formData.deadline);
      submitData.append('additionalInfo', formData.additionalInfo);
      submitData.append('agreementAccepted', 'true');
      submitData.append('serviceType', 'ots');
      submitData.append('deliveryDestination', activeDestination || '');
      submitData.append('deliveryCharge', deliveryCharge);
      submitData.append('totalAmount', totalAmount);
      if (window.otsPaymentResult) {
        submitData.append('paymentId', window.otsPaymentResult.transactionId || '');
        submitData.append('paymentStatus', 'completed');
      }
      if (formData.workType === 'assignment') {
        submitData.append('pages', parseInt(formData.pages));
      } else {
        submitData.append('pages', parseInt(formData.pages));
      }
      if (uploadedFile) submitData.append('file', uploadedFile);

      const response = await learnEarnAPI.customerWork.submit(submitData);
      alert(`✅ Success! ${response.message || 'Your request has been submitted successfully!'}`);

      // Reset form
      setFormData({ name:'', address:'', phone:'', whatsapp:'', sameAsPhone:false, email:'', workType:'assignment', pages:'', amount:'', deadline:'', additionalInfo:'', agreementAccepted:false });
      setUploadedFile(null);
      setServiceType('');
      setDeliveryType('');
      setDestination('');
      setJournalDestination('');
      window.otsPaymentResult = null;
    } catch (error) {
      alert(`❌ Error: ${error.message || 'Failed to submit. Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAgreementAccept = async (paymentResult) => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();

      // Append form data
      submitData.append('name', formData.name);
      submitData.append('address', formData.address);
      submitData.append('phone', formData.phone);
      submitData.append('whatsapp', formData.whatsapp);
      submitData.append('email', formData.email);
      submitData.append('workType', formData.workType);
      submitData.append('deadline', formData.deadline);
      submitData.append('additionalInfo', formData.additionalInfo);
      submitData.append('agreementAccepted', 'true');
      submitData.append('serviceType', 'ots'); // Add service type
      
      // Append payment information
      if (paymentResult) {
        submitData.append('paymentId', paymentResult.transactionId);
        submitData.append('paymentStatus', 'completed');
      }

      // Add conditional fields based on work type
      if (formData.workType === 'assignment') {
        submitData.append('pages', parseInt(formData.pages));
      } else {
        submitData.append('amount', parseFloat(formData.amount));
      }

      // Append the file
      if (uploadedFile) {
        submitData.append('file', uploadedFile);
      }

      const response = await learnEarnAPI.customerWork.submit(submitData);

      alert(`Success! ${response.message}\n\nPayment ID: ${paymentResult?.transactionId}\n\nYour OTS work request has been posted to the Workshop section. Students can now apply to complete your work!`);
      setShowAgreement(false);

      // Reset form
      setFormData({
        name: '',
        address: '',
        phone: '',
        whatsapp: '',
        sameAsPhone: false,
        email: '',
        workType: 'assignment',
        pages: '',
        amount: '',
        deadline: '',
        additionalInfo: '',
        agreementAccepted: false,
      });
      setUploadedFile(null);

    } catch (error) {
      console.error('Submission error:', error);
      alert(`Error: ${error.message || 'Failed to submit request. Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Use the new pricing utility — override rate for urgent service
  const baseRate = (() => {
    if (serviceType === 'urgent' && daysUntilDeadline && daysUntilDeadline <= 3) {
      return formData.workType === 'journal'
        ? getUrgentJournalRatePerPage(daysUntilDeadline)
        : getUrgentRatePerPage(daysUntilDeadline);
    }
    return formData.workType === 'journal' ? 5 : 7;
  })();

  const calculatedAmount = (parseInt(formData.pages) || 0) * baseRate;

  // Delivery charge calculation
  const activeDestination = formData.workType === 'journal' ? journalDestination : destination;
  const baseDeliveryCharge = getDeliveryCharge(activeDestination) ?? 0;
  const deliveryCharge = formData.workType === 'journal'
    ? baseDeliveryCharge * 2
    : deliveryType === 'college-paper'
      ? baseDeliveryCharge * 2
      : baseDeliveryCharge;

  const totalAmount = calculatedAmount + deliveryCharge;

  // Payment handler function - now handles both payment and form submission
  const handlePaymentClick = () => {
    if (!validateForm()) {
      alert('Please fill in all required fields before proceeding to payment');
      return;
    }

    setIsProcessingPayment(true);

    const paymentData = {
      amount: calculatedAmount,
      workType: formData.workType,
      customerName: formData.name,
      customerEmail: formData.email,
      customerPhone: formData.phone,
      otsSubmissionId: `ots_${Date.now()}`,
    };

    initiateOTSPayment(
      paymentData,
      (paymentResult) => {
        // Payment successful - now show agreement dialog
        setIsProcessingPayment(false);
        // Store payment result temporarily to pass to form submission
        window.otsPaymentResult = paymentResult;
        setShowAgreement(true);
      },
      (error) => {
        // Payment failed
        setIsProcessingPayment(false);
        
        // Show user-friendly error message
        const errorMessage = error.message || 'Payment failed. Please try again.';
        
        if (errorMessage.includes('not available') || errorMessage.includes('Route not found')) {
          alert(`⚠️ Payment Service Unavailable\n\nThe payment gateway is currently being set up. Please try one of these options:\n\n1. Contact support for manual payment\n2. Try again later\n3. Email us at support@agoinnovations.com\n\nYour form data has been saved.`);
        } else if (errorMessage.includes('cancelled')) {
          alert('❌ Payment Cancelled\n\nYou cancelled the payment. You can try again when ready.');
        } else {
          alert(`❌ Payment Failed\n\n${errorMessage}\n\nPlease try again or contact support if the issue persists.`);
        }
      }
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-orange-100 to-red-100 px-3 sm:px-6 sm:py-8 relative overflow-x-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-orange-300/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-red-300/30 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="pt-4 sm:pt-8"
        >
          <Breadcrumb items={breadcrumbItems} />
          <Button
            variant="ghost"
            onClick={() => navigate('/learn/customer-work')}
            className="mb-4 sm:mb-6 hover:bg-orange-100 text-orange-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Customer Work
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border shadow-2xl bg-white">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <CardTitle className="text-xl sm:text-2xl">OTS Service Portal</CardTitle>
                  <CardDescription className="text-orange-100 text-xs sm:text-sm">
                    One Time Service - Submit your assignment or journal work request
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 md:p-8">
              {/* Privacy Notice */}
              <div className="mb-5 sm:mb-6 p-3 sm:p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <div className="flex items-start sm:items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-emerald-700 font-semibold text-sm">🔒 Privacy Protected</p>
                    <p className="text-emerald-600 text-xs mt-0.5">Your personal information and uploaded documents are securely protected and kept strictly confidential.</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Details */}
                <div className="space-y-4 sm:space-y-5">
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-orange-700">Personal Details</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <Label htmlFor="name" className="text-foreground flex items-center gap-2">
                        <User className="w-4 h-4 text-accent" />
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className={`mt-2 ${errors.name ? 'border-destructive' : 'border-input focus:border-primary'} bg-background`}
                        placeholder="Enter your full name"
                      />
                      {errors.name && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.name}</p>}
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-foreground flex items-center gap-2">
                        <Mail className="w-4 h-4 text-accent" />
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className={`mt-2 ${errors.email ? 'border-destructive' : 'border-input focus:border-primary'} bg-background`}
                        placeholder="your.email@example.com"
                      />
                      {errors.email && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.email}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-foreground flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-accent" />
                      Address *
                    </Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleChange('address', e.target.value)}
                      className={`mt-2 ${errors.address ? 'border-destructive' : 'border-input focus:border-primary'} bg-background`}
                      placeholder="Enter your complete address"
                    />
                    {errors.address && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.address}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <Label htmlFor="phone" className="text-foreground flex items-center gap-2">
                        <Phone className="w-4 h-4 text-accent" />
                        Phone Number *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleChange('phone', value);
                          if (formData.sameAsPhone) {
                            handleChange('whatsapp', value);
                          }
                        }}
                        className={`mt-2 border-2 ${errors.phone ? 'border-red-400' : 'border-orange-200 focus:border-orange-500'} bg-white/70`}
                        placeholder="+91 1234567890"
                        maxLength={10}
                      />
                      {errors.phone && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.phone}</p>}
                    </div>

                    <div>
                      <Label htmlFor="whatsapp" className="text-foreground flex items-center gap-2">
                        <Phone className="w-4 h-4 text-emerald-500" />
                        WhatsApp Number
                      </Label>
                      <Input
                        id="whatsapp"
                        type="tel"
                        value={formData.whatsapp}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          handleChange('whatsapp', value);
                        }}
                        disabled={formData.sameAsPhone}
                        className="mt-2 border-2 border-green-200 focus:border-green-500 bg-white/70 disabled:bg-gray-100"
                        placeholder="+91 1234567890"
                        maxLength={10}
                      />
                      {errors.whatsapp && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.whatsapp}</p>}
                      <div className="flex items-center mt-2">
                        <Checkbox
                          id="sameAsPhone"
                          checked={formData.sameAsPhone}
                          onCheckedChange={(checked) => handleChange('sameAsPhone', checked)}
                          className="border-emerald-500"
                        />
                        <Label htmlFor="sameAsPhone" className="ml-2 cursor-pointer text-sm text-muted-foreground">
                          Same as phone number
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Work Section */}
                <div className="space-y-4 sm:space-y-5">
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-orange-700">Work Details</h3>
                  </div>

                  {/* WORK TYPE */}
                  <div>
                    <Label className="text-foreground font-semibold mb-3 block">Which work do you want to get done? *</Label>
                    <RadioGroup
                      value={formData.workType}
                      onValueChange={(value) => { handleChange('workType', value); setUploadedFile(null); }}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
                    >
                      <div className={`relative flex items-center space-x-3 p-5 border rounded-xl cursor-pointer transition-all ${formData.workType === 'assignment' ? 'border-blue-500 bg-blue-50' : 'border-border bg-card hover:border-blue-300'}`}>
                        <RadioGroupItem value="assignment" id="assignment" className="border-blue-500" />
                        <Label htmlFor="assignment" className="cursor-pointer flex-1">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <span className="font-semibold text-foreground">Assignment</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Get your assignment completed</p>
                        </Label>
                      </div>
                      <div className={`relative flex items-center space-x-3 p-5 border rounded-xl cursor-pointer transition-all ${formData.workType === 'journal' ? 'border-emerald-500 bg-emerald-50' : 'border-border bg-card hover:border-emerald-300'}`}>
                        <RadioGroupItem value="journal" id="journal" className="border-emerald-500" />
                        <Label htmlFor="journal" className="cursor-pointer flex-1">
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-emerald-600" />
                            <span className="font-semibold text-foreground">Journal</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Get your journal written</p>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* SERVICE TYPE BUTTONS */}
                  <div>
                    <Label className="text-foreground font-semibold mb-3 block">Select Service Type *</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {/* Normal Service */}
                      <button
                        type="button"
                        onClick={() => { setServiceType('normal'); setUploadedFile(null); handleChange('deadline', ''); }}
                        className={`p-4 sm:p-5 rounded-xl border-2 text-left transition-all duration-200 ${serviceType === 'normal' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300'}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Calendar className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-bold text-blue-700 text-base">Normal Service</span>
                          {serviceType === 'normal' && <span className="ml-auto text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">✓ Selected</span>}
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed mb-2">
                          Choose when you have <span className="font-semibold text-blue-600">4 or more days</span> until your deadline.
                        </p>
                        <p className="text-xs font-semibold text-blue-600">
                          💰 {formData.workType === 'journal' ? '₹5 per page' : '₹7 per page'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">✔ Standard delivery &nbsp;✔ Best quality &nbsp;✔ No rush charges</p>
                      </button>

                      {/* Urgent Service */}
                      <button
                        type="button"
                        onClick={() => { setServiceType('urgent'); setUploadedFile(null); handleChange('deadline', ''); }}
                        className={`p-4 sm:p-5 rounded-xl border-2 text-left transition-all duration-200 ${serviceType === 'urgent' ? 'border-red-500 bg-red-50 shadow-md' : 'border-gray-200 bg-white hover:border-red-300'}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Zap className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-bold text-red-600 text-base">Urgent Service</span>
                          {serviceType === 'urgent' && <span className="ml-auto text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">✓ Selected</span>}
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed mb-2">
                          Choose when you need delivery within <span className="font-semibold text-red-600">1–3 days</span>.
                        </p>
                        <p className="text-xs font-semibold text-red-600 mb-1">💰 Urgent Rates (per page):</p>
                        {formData.workType === 'journal' ? (
                          <p className="text-xs text-gray-600">• 3 days → ₹7/page &nbsp;• 2 days → ₹8/page &nbsp;• 1 day → ₹9/page</p>
                        ) : (
                          <p className="text-xs text-gray-600">• 3 days → ₹9/page &nbsp;• 2 days → ₹10/page &nbsp;• 1 day → ₹12/page</p>
                        )}
                      </button>
                    </div>
                    {!serviceType && <p className="text-orange-500 text-xs mt-2 font-medium">⚠ Please select a service type to proceed.</p>}
                  </div>

                  {/* DEADLINE — shown only after service type selected */}
                  {serviceType && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                      <Label htmlFor="deadline" className="text-foreground font-semibold flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-orange-500" />
                        Completion Deadline *
                      </Label>
                      <div className="relative mt-2">
                        <Input
                          id="deadline"
                          type="date"
                          value={formData.deadline}
                          onChange={(e) => { handleChange('deadline', e.target.value); setUploadedFile(null); }}
                          min={new Date().toISOString().split('T')[0]}
                          className={`border ${errors.deadline ? 'border-destructive' : 'border-orange-200 focus:border-orange-500'} bg-background`}
                        />
                        <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-orange-500 pointer-events-none" />
                      </div>
                      {errors.deadline && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.deadline}</p>}

                      {/* Deadline status badge */}
                      {formData.deadline && daysUntilDeadline !== null && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className={`mt-3 p-3 rounded-xl flex items-center gap-2 text-sm font-medium ${
                            uploadAllowed
                              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                              : 'bg-red-50 border border-red-200 text-red-700'
                          }`}
                        >
                          <span>{uploadAllowed ? '✅' : '⛔'}</span>
                          <span>
                            {daysUntilDeadline === 0 ? 'Deadline is today' : `${daysUntilDeadline} day(s) remaining`}
                            {serviceType === 'urgent' && uploadAllowed && formData.workType === 'assignment' &&
                              ` · Rate: ₹${getUrgentRatePerPage(daysUntilDeadline)}/page`}
                          </span>
                        </motion.div>
                      )}

                      {/* Block warning */}
                      {uploadBlockMessage && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-3 p-4 bg-red-50 border-2 border-red-300 rounded-xl text-red-700 text-sm font-medium"
                        >
                          {uploadBlockMessage}
                        </motion.div>
                      )}
                    </motion.div>
                  )}

                  {/* ASSIGNMENT FIELDS */}
                  {formData.workType === 'assignment' && serviceType && formData.deadline && (
                    <motion.div
                      className="space-y-5 p-6 bg-blue-50 rounded-2xl border border-blue-200"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div>
                        <Label htmlFor="pages" className="text-foreground font-semibold">Number of Pages *</Label>
                        <Input
                          id="pages"
                          type="number"
                          min="1"
                          value={formData.pages}
                          onChange={(e) => handleChange('pages', e.target.value)}
                          className={`mt-2 ${errors.pages ? 'border-destructive' : 'border-blue-200 focus:border-blue-500'} bg-background`}
                          placeholder="Enter number of pages"
                        />
                        <p className="text-sm text-blue-600 mt-2 font-semibold">
                          💰 Rate: ₹{baseRate}/page
                          {serviceType === 'urgent' && uploadAllowed && <span className="ml-2 text-red-500 text-xs font-normal">(Urgent surcharge)</span>}
                        </p>
                        {errors.pages && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.pages}</p>}
                      </div>

                      {formData.pages && parseInt(formData.pages) > 0 && (
                        <motion.div
                          className="bg-blue-600 p-6 rounded-2xl text-white shadow-lg"
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                        >
                          <p className="text-sm opacity-90">Total Amount to be Paid</p>
                          <p className="text-4xl font-bold mt-2">₹{calculatedAmount.toFixed(1)}</p>
                          <p className="text-xs opacity-80 mt-2">{formData.pages} pages × ₹{baseRate}/page</p>
                        </motion.div>
                      )}

                      {/* Upload — gated by deadline validity */}
                      <div>
                        <Label className="text-foreground font-semibold flex items-center gap-2">
                          <Upload className="w-4 h-4 text-blue-600" />
                          Upload Questions *
                        </Label>
                        {/* Always mount FileUpload so internal file state is never lost on deadline change */}
                        <div className={`mt-3 ${!uploadAllowed ? 'hidden' : ''}`}>
                          <FileUpload onFileSelect={handleFileSelect} accept=".pdf,.jpg,.jpeg,.png" maxSize={10} />
                          <p className="text-xs text-blue-600 mt-2 font-medium">📁 Upload PDF or image (JPG, PNG) of questions</p>
                        </div>
                        {!uploadAllowed && (
                          <div className="mt-3 p-5 bg-red-50 border-2 border-dashed border-red-300 rounded-xl text-center">
                            <p className="text-red-500 font-semibold text-sm">🔒 Upload Locked</p>
                            <p className="text-red-400 text-xs mt-1">Fix your deadline to unlock uploading.</p>
                          </div>
                        )}
                        {errors.file && !uploadedFile && <p className="text-destructive text-sm mt-2 flex items-center gap-1">⚠ {errors.file}</p>}
                      </div>
                    </motion.div>
                  )}

                  {/* JOURNAL FIELDS */}
                  {formData.workType === 'journal' && serviceType && formData.deadline && (
                    <motion.div
                      className="space-y-5 p-6 bg-emerald-50 rounded-2xl border border-emerald-200"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      {/* Service type info banner */}
                      <div className={`flex items-center gap-3 p-3 rounded-xl text-sm font-medium ${serviceType === 'urgent' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
                        <span>{serviceType === 'urgent' ? '⚡' : '📅'}</span>
                        <span>
                          {serviceType === 'urgent'
                            ? `Urgent Service · Rate: ₹${baseRate}/page`
                            : `Normal Service · Rate: ₹${baseRate}/page`}
                        </span>
                      </div>

                      {/* Pages input */}
                      <div>
                        <Label htmlFor="journal-pages" className="text-foreground font-bold text-base flex items-center gap-2">
                          <BookOpen className="w-5 h-5 text-emerald-600" />
                          Enter the number of pages you want to get written
                        </Label>
                        <p className="text-xs text-emerald-700 mt-1 mb-3 leading-relaxed">
                          📝 Count all pages including <span className="font-semibold">writing pages</span> and <span className="font-semibold">drawing/diagram pages</span> in the total.
                        </p>
                        <Input
                          id="journal-pages"
                          type="number"
                          min="1"
                          value={formData.pages}
                          onChange={(e) => handleChange('pages', e.target.value)}
                          className={`mt-1 ${errors.pages ? 'border-destructive' : 'border-emerald-200 focus:border-emerald-500'} bg-background`}
                          placeholder="e.g. 20 (writing pages + drawing pages)"
                        />
                        <p className="text-sm text-emerald-600 mt-2 font-semibold">
                          💰 Rate: ₹{baseRate}/page
                          {serviceType === 'urgent' && <span className="ml-2 text-red-500 text-xs font-normal">(Urgent surcharge)</span>}
                        </p>
                        {errors.pages && <p className="text-destructive text-sm mt-1 flex items-center gap-1">⚠ {errors.pages}</p>}
                      </div>

                      {/* Total amount display */}
                      {formData.pages && parseInt(formData.pages) > 0 && (
                        <motion.div
                          className="bg-emerald-600 p-6 rounded-2xl text-white shadow-lg"
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                        >
                          <p className="text-sm opacity-90">Total Amount to be Paid</p>
                          <p className="text-4xl font-bold mt-2">₹{(parseInt(formData.pages) * baseRate).toFixed(1)}</p>
                          <p className="text-xs opacity-80 mt-2">{formData.pages} pages × ₹{baseRate}/page</p>
                        </motion.div>
                      )}

                      {/* Upload — gated by deadline validity */}
                      <div>
                        <Label className="text-foreground font-bold text-base flex items-center gap-2">
                          <Upload className="w-4 h-4 text-emerald-600" />
                          Upload Journal Pages *
                        </Label>
                        <p className="text-xs text-emerald-700 mt-1 mb-1 leading-relaxed">
                          📸 Convert all your images to a single PDF and upload — include all pages you want written <span className="font-semibold">and</span> pages where diagrams/drawings need to be made.
                        </p>
                        {/* Always mount FileUpload so internal file state is never lost on deadline change */}
                        <div className={`mt-3 ${!uploadAllowed ? 'hidden' : ''}`}>
                          <FileUpload onFileSelect={handleFileSelect} accept=".pdf,.jpg,.jpeg,.png" maxSize={10} />
                          <p className="text-xs text-emerald-600 mt-2 font-medium">📁 PDF preferred · JPG / PNG also accepted</p>
                        </div>
                        {!uploadAllowed && (
                          <div className="mt-3 p-5 bg-red-50 border-2 border-dashed border-red-300 rounded-xl text-center">
                            <p className="text-red-500 font-semibold text-sm">🔒 Upload Locked</p>
                            <p className="text-red-400 text-xs mt-1">Fix your deadline to unlock uploading.</p>
                          </div>
                        )}
                        {errors.file && !uploadedFile && <p className="text-destructive text-sm mt-2 flex items-center gap-1">⚠ {errors.file}</p>}
                      </div>
                    </motion.div>
                  )}

                  {/* Additional Information Section */}
                  <div className="space-y-4 sm:space-y-5">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-white" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-orange-700">Additional Information</h3>
                    </div>

                    <div className="p-6 bg-orange-50 rounded-2xl border border-orange-200">
                      <Label htmlFor="additionalInfo" className="text-foreground font-semibold flex items-center gap-2 mb-3">
                        <FileText className="w-4 h-4 text-orange-500" />
                        Specific Requirements or Details
                        <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                      </Label>
                      <textarea
                        id="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={(e) => handleChange('additionalInfo', e.target.value)}
                        className="w-full min-h-[120px] p-4 border border-input focus:border-orange-400 bg-background rounded-xl resize-vertical transition-all"
                        placeholder="Please provide any specific requirements, instructions, or additional details about your work...

Examples:
• Specific formatting requirements
• Reference style (APA, MLA, etc.)
• Special instructions
• Preferred writing style
• Any other important details"
                        rows={6}
                      />
                      <p className="text-xs text-primary mt-2 flex items-center gap-1">
                        💡 <span>The more details you provide, the better we can serve your requirements</span>
                      </p>
                    </div>
                  </div>

                  {/* Payment Gateway Section */}
                  {/* DELIVERY SYSTEM — Assignment only */}
                  {formData.workType === 'assignment' && (
                    <DeliverySection
                      deliveryType={deliveryType}
                      setDeliveryType={setDeliveryType}
                      destination={destination}
                      setDestination={setDestination}
                      trainType={trainType}
                      setTrainType={handleTrainTypeChange}
                      errors={errors}
                    />
                  )}

                  {/* DELIVERY SYSTEM — Journal only */}
                  {formData.workType === 'journal' && (
                    <JournalDeliverySection
                      destination={journalDestination}
                      setDestination={setJournalDestination}
                      trainType={journalTrainType}
                      setTrainType={handleJournalTrainTypeChange}
                      errors={errors}
                    />
                  )}

                  {/* PAYMENT SUMMARY + SUBMIT */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-3"
                  >
                    {/* Total breakdown card — shown when pages are filled */}
                    {(parseInt(formData.pages) > 0 || deliveryCharge > 0) && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-2xl p-4 sm:p-5 overflow-hidden"
                      >
                        <h4 className="font-bold text-orange-700 text-base mb-3 flex items-center gap-2">
                          🧾 Payment Summary
                        </h4>

                        <div className="space-y-2">
                          {/* Work charges */}
                          <div className="flex justify-between items-start gap-2 text-sm">
                            <span className="text-gray-600 min-w-0 flex-1">
                              {formData.workType === 'assignment' ? 'Assignment' : 'Journal'} Work
                              {parseInt(formData.pages) > 0 && (
                                <span className="text-gray-400 ml-1 block sm:inline">({formData.pages} pages × ₹{baseRate})</span>
                              )}
                            </span>
                            <span className="font-semibold text-gray-800 flex-shrink-0">
                              {calculatedAmount > 0 ? `₹${calculatedAmount.toFixed(1)}` : '—'}
                            </span>
                          </div>

                          {/* Delivery charges */}
                          <div className="flex justify-between items-start gap-2 text-sm">
                            <span className="text-gray-600 min-w-0 flex-1">
                              Delivery Charges
                              {activeDestination && (
                                <span className="text-gray-400 ml-1 block sm:inline truncate">
                                  (Kalyan → {activeDestination})
                                </span>
                              )}
                              {(formData.workType === 'journal' || deliveryType === 'college-paper') && (
                                <span className="text-purple-500 ml-1 text-xs">(×2)</span>
                              )}
                            </span>
                            <span className="font-semibold text-gray-800 flex-shrink-0">
                              {activeDestination
                                ? deliveryCharge === 0 ? 'Free' : `₹${deliveryCharge}`
                                : '—'}
                            </span>
                          </div>

                          {/* Divider */}
                          <div className="border-t border-orange-200 pt-2 mt-2">
                            <div className="flex justify-between items-center gap-2">
                              <span className="font-bold text-orange-700 text-base">Total Amount</span>
                              <span className="font-black text-orange-600 text-xl sm:text-2xl flex-shrink-0">
                                {totalAmount > 0 ? `₹${totalAmount.toFixed(1)}` : '—'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* BUTTONS — shown only when total is calculated */}
                    {totalAmount > 0 && (
                      <div className="space-y-3 pb-4">
                        {/* GREEN PAY BUTTON — always visible when total > 0 */}
                        {!paymentDone && (
                          <button
                            type="button"
                            disabled={
                              isProcessingPayment ||
                              !formData.name.trim() ||
                              !formData.address.trim() ||
                              !formData.phone.trim() ||
                              !formData.email.trim() ||
                              !serviceType ||
                              !formData.deadline ||
                              !uploadAllowed ||
                              !uploadedFile ||
                              (formData.workType === 'assignment' && (!formData.pages || parseInt(formData.pages) <= 0)) ||
                              (formData.workType === 'journal' && (!formData.pages || parseInt(formData.pages) <= 0))
                            }
                            onClick={async () => {
                              if (!validateForm()) return;
                              setIsProcessingPayment(true);
                              setPaymentError(null);
                              initiateOTSPayment(
                                {
                                  amount: totalAmount,
                                  customerName: formData.name,
                                  customerEmail: formData.email,
                                  customerPhone: formData.phone,
                                  orderNote: `AGO OTS - ${formData.workType} (${formData.pages} pages) - Delivery to ${activeDestination || 'Kalyan'}`,
                                  otsSubmissionId: `ots_${Date.now()}`,
                                },
                                (paymentResult) => {
                                  setIsProcessingPayment(false);
                                  setPaymentError(null);
                                  window.otsPaymentResult = paymentResult;
                                  setPaymentDone(true);
                                },
                                (error) => {
                                  setIsProcessingPayment(false);
                                  if (error.message === 'cancelled') {
                                    setPaymentError('cancelled');
                                  } else {
                                    setPaymentError('failed');
                                  }
                                }
                              );
                            }}
                            className="w-full py-4 sm:py-5 text-lg sm:text-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-2xl shadow-xl hover:shadow-2xl transition-all disabled:opacity-40 disabled:cursor-not-allowed overflow-hidden"
                          >
                            {isProcessingPayment ? (
                              <span className="flex items-center justify-center gap-2">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                Processing Payment...
                              </span>
                            ) : (
                              <span className="flex items-center justify-center gap-1.5 flex-wrap px-2">
                                <span>💳</span>
                                <span>Pay Now</span>
                                <span className="font-black">₹{totalAmount.toFixed(1)}</span>
                              </span>
                            )}
                          </button>
                        )}

                        {/* Inline cancel / error feedback */}
                        {paymentError === 'cancelled' && (
                          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-300 rounded-xl text-sm">
                            <span className="text-amber-500 text-base">ℹ️</span>
                            <div>
                              <p className="text-amber-700 font-semibold">Payment cancelled</p>
                              <p className="text-amber-600 text-xs">No money was deducted. Click Pay Now whenever you're ready.</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPaymentError(null)}
                              className="ml-auto text-amber-400 hover:text-amber-600 text-lg leading-none"
                            >×</button>
                          </div>
                        )}

                        {paymentError === 'failed' && (
                          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-300 rounded-xl text-sm">
                            <span className="text-red-500 text-base">❌</span>
                            <div>
                              <p className="text-red-700 font-semibold">Payment failed</p>
                              <p className="text-red-600 text-xs">Please try again or contact support if the issue persists.</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setPaymentError(null)}
                              className="ml-auto text-red-400 hover:text-red-600 text-lg leading-none"
                            >×</button>
                          </div>
                        )}

                        {/* SUBMIT BUTTON — always visible, locked until payment is done */}
                        <div className="relative">
                          {/* Payment success badge — shown after payment */}
                          {paymentDone && (
                            <motion.div
                              initial={{ opacity: 0, y: -6 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="flex items-center gap-2 p-3 mb-2 bg-emerald-50 border border-emerald-300 rounded-xl"
                            >
                              <span className="text-emerald-600 text-lg">✅</span>
                              <div>
                                <p className="text-emerald-700 font-bold text-sm">Payment Successful!</p>
                                <p className="text-emerald-600 text-xs">
                                  Txn ID: {window.otsPaymentResult?.transactionId || '—'} · Now submit your request below.
                                </p>
                              </div>
                            </motion.div>
                          )}

                          <Button
                            type="submit"
                            disabled={!paymentDone || isSubmitting}
                            className={`w-full py-5 text-xl font-bold rounded-2xl shadow-xl transition-all border-0 ${
                              paymentDone
                                ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white hover:shadow-2xl cursor-pointer'
                                : 'bg-gradient-to-r from-orange-400 to-red-400 text-white cursor-not-allowed opacity-50'
                            }`}
                          >
                            {isSubmitting ? (
                              <span className="flex items-center justify-center gap-2">
                                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                Submitting...
                              </span>
                            ) : (
                              <span className="flex items-center justify-center gap-2">
                                {!paymentDone && <span>🔒</span>}
                                {paymentDone ? '📨 Submit Request' : 'Submit Request'}
                              </span>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </motion.div>

                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <AgreementDialog
        open={showAgreement}
        onOpenChange={setShowAgreement}
        onAccept={() => handleAgreementAccept(window.otsPaymentResult)}
        workType={formData.workType}
        amount={calculatedAmount}
      />
    </div>
  );
}